import { useCallback, useEffect, useRef } from 'react';
import { SearchProvider } from '../contexts/SearchProvider';
import shopify from '../../js/shopify';
import helpers from '../../js/helpers';
import algoliasearch from 'algoliasearch';

/**
 * Creates a provider for Algolia search implementation
 * 
 * @param {Object} props - The props for the AlgoliaSearchProvider component
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.settings - Settings for the Algolia search
 * @param {string} props.searchType - Type of search ('search', 'plp', 'header')
 * @returns {JSX.Element} A SearchProvider with Algolia-specific implementation
 */
export function AlgoliaSearchProvider({ children, settings, searchType = 'search' }) {
  // Create refs to maintain state across re-renders
  const providerRef = useRef({
    client: null,
    initialized: false,
    isProcessingRequest: false,
    requestType: '',
    currentRequest: null,
    headerResultsTotal: null,
    productsPerPage: null,
    defaultSort: null,
    isFiltering: false,
    sortOptions: [],
    usedFacets: [],
    defaultSearchParams: {
      hitsPerPage: 20,
      distinct: 1,
      facetingAfterDistinct: true,
      attributesToHighlight: [],
      attributesToRetrieve: ['*'],
    }
  });

  const provider = providerRef.current;

  // Initialize the provider
  const initialize = useCallback((searchSettings) => {
    if (provider.initialized) return;

    if (searchSettings) {
      // Extract facets from settings
      provider.usedFacets = searchSettings?.facets?.replaceAll("\n", "").split(",") || [];
      
      // Set attributes to retrieve
      const attributes = searchSettings?.attributes?.replaceAll("\n", "").split(",") || ['*'];
      provider.defaultSearchParams.attributesToRetrieve = attributes;
      
      // Set products per page
      provider.productsPerPage = searchSettings.products_per_page || 20;
      
      // Set header results total
      provider.headerResultsTotal = searchSettings?.results_returned_header || 4;

      // Parse index settings
      const indexes = searchSettings?.search_indexes?.split(',').reduce((acc, pair) => {
        const [key, value] = pair.split(':').map(str => str.trim());
        acc[key] = value;
        return acc;
      }, {}) || {};

      // Set default sort
      provider.defaultSort = searchSettings?.default_sort || 'default';

      // Create config object
      const config = {
        app: searchSettings.app_id || null,
        key: searchSettings.api_key || null,
        index: indexes,
      };

      // Parse sort options
      provider.sortOptions = searchSettings?.sort_indicies?.split(',').map(pair => {
        const [key, value] = pair.split(':').map(str => str.trim());
        return { key, value };
      }) || [];

      // Initialize Algolia client
      provider.client = algoliasearch(config.app, config.key);
      provider.initialized = true;

      // Set up event listeners
      window.addEventListener('searchrequest', handleSearchRequest);
      window.addEventListener('searchrequest-articles', handleSearchRequest);
      window.addEventListener('searchrequest-suggestions', handleSuggestionSearchRequest);
      window.addEventListener('searchrequest-filter-applied', handleFilterRequest);
      window.addEventListener('searchrequest-sort-applied', handleSearchRequest);
      window.addEventListener('searchrequest-nextpage', handlePaginationRequest);

      return () => {
        window.removeEventListener('searchrequest', handleSearchRequest);
        window.removeEventListener('searchrequest-articles', handleSearchRequest);
        window.removeEventListener('searchrequest-suggestions', handleSuggestionSearchRequest);
        window.removeEventListener('searchrequest-filter-applied', handleFilterRequest);
        window.removeEventListener('searchrequest-sort-applied', handleSearchRequest);
        window.removeEventListener('searchrequest-nextpage', handlePaginationRequest);
      };
    }
  }, []);

  // Handle search requests
  const handleSearchRequest = useCallback(async (event) => {
    const { query, type, filters, index, productsPerPage, isFiltering } = event.detail;

    if (provider.isProcessingRequest) {
      cancelCurrentRequest();
    }

    provider.isProcessingRequest = true;
    provider.requestType = type;

    if (isFiltering) {
      provider.isFiltering = isFiltering;
    }

    if (provider.requestType === 'search') {
      window.dispatchEvent(new CustomEvent('show-spinner', {
        detail: { id: 'spinner-search' },
      }));
    } else if (provider.requestType === 'plp') {
      window.dispatchEvent(new CustomEvent('show-spinner', {
        detail: { id: 'spinner-collection' },
      }));
    }

    // Determine the search index to use
    let searchIndex = null;
    if (provider.requestType === 'header') {
      searchIndex = provider.client.initIndex(index in provider.config.index ? index : 'default');
    } else {
      searchIndex = provider.client.initIndex(index in provider.config.index ? index : provider.defaultSort);
    }

    // Set hits per page based on request type
    if (provider.requestType === 'header') {
      provider.defaultSearchParams.hitsPerPage = provider.headerResultsTotal;
    } else {
      provider.defaultSearchParams.hitsPerPage = productsPerPage || provider.productsPerPage;
    }

    // Build custom search params
    const customSearchParams = buildCustomSearchParams(filters);
    
    // Perform the search based on index
    try {
      let results = null;
      switch (searchIndex) {
        case 'articles':
          results = await algoliaArticleSearch(query, customSearchParams, {}, searchIndex);
          break;
        default:
          results = await algoliaProductSearch(query, customSearchParams, {}, searchIndex);
          break;
      }
      
      window.dispatchEvent(new CustomEvent('loading', {
        detail: { value: false },
      }));
      
      return results;
    } catch (error) {
      console.error('Error with Algolia search:', error);
      provider.isProcessingRequest = false;
      return null;
    }
  }, []);

  // Handle suggestion search requests
  const handleSuggestionSearchRequest = useCallback(async (event) => {
    const { query, type, filters, index } = event.detail;
    
    if (provider.isProcessingRequest) {
      cancelCurrentRequest();
    }

    const customSearchParams = buildCustomSearchParams(filters);

    const suggestionsQuery = [];
    index.forEach(indexKey => {
      suggestionsQuery.push({
        indexName: provider.config.index[indexKey],
        query: query,
        params: {
          ...provider.defaultSearchParams,
          facets: provider.usedFacets,
          ...customSearchParams,
        }
      });
    });

    provider.isProcessingRequest = true;
    provider.requestType = type;

    try {
      const results = await algoliaSuggestionsSearch(suggestionsQuery);
      return results;
    } catch (error) {
      console.error('Error with Algolia suggestions search:', error);
      provider.isProcessingRequest = false;
      return null;
    }
  }, []);

  // Handle filter requests
  const handleFilterRequest = useCallback(async (event) => {
    const { query, type, filters, index, isFilteringRequest } = event.detail;

    if (provider.isProcessingRequest) {
      cancelCurrentRequest();
    }
    
    provider.isProcessingRequest = true;
    provider.requestType = type;
    provider.isFiltering = isFilteringRequest;

    const searchIndex = index in provider.config.index ? index : provider.defaultSort;

    if (provider.requestType === 'search') {
      window.dispatchEvent(new CustomEvent('show-spinner', {
        detail: { id: 'spinner-search' },
      }));
    } else if (provider.requestType === 'plp') {
      window.dispatchEvent(new CustomEvent('show-spinner', {
        detail: { id: 'spinner-collection' },
      }));
    }
    
    const customSearchParams = buildCustomSearchParams(filters);
    
    try {
      await algoliaProductSearch(query, customSearchParams, {}, searchIndex, true);
    } catch (error) {
      console.error('Error with Algolia filter request:', error);
      provider.isProcessingRequest = false;
    }
  }, []);

  // Handle pagination requests
  const handlePaginationRequest = useCallback(async (event) => {
    const { query, type, filters, page, index, productsPerPage } = event.detail;

    if (provider.isProcessingRequest) {
      cancelCurrentRequest();
    }
    
    provider.isProcessingRequest = true;
    provider.requestType = type;

    if (provider.requestType === 'search') {
      window.dispatchEvent(new CustomEvent('show-spinner', {
        detail: { id: 'spinner-search' },
      }));
    } else if (provider.requestType === 'plp') {
      window.dispatchEvent(new CustomEvent('show-spinner', {
        detail: { id: 'spinner-collection' },
      }));
    }

    if (provider.requestType === 'header') {
      provider.defaultSearchParams.hitsPerPage = provider.headerResultsTotal;
    } else {
      provider.defaultSearchParams.hitsPerPage = productsPerPage || provider.productsPerPage;
    }

    const searchIndex = index in provider.config.index ? index : provider.defaultSort;
    const customSearchParams = buildCustomSearchParams(filters, page);
    
    try {
      const results = await algoliaProductSearch(query, customSearchParams, {}, searchIndex);
      return results;
    } catch (error) {
      console.error('Error with Algolia pagination request:', error);
      provider.isProcessingRequest = false;
      return null;
    }
  }, []);

  // Helper function to build custom search parameters
  const buildCustomSearchParams = useCallback((filters, page) => {
    let customSearchParams = {};
    
    if (filters) {
      if (filters.collection) {
        // Optionally add collection filter
      } else {
        // Add other filters
      }
    }
    
    if (page) {
      customSearchParams.page = page;
    }
    
    return customSearchParams;
  }, []);

  // Helper function to cancel current request
  const cancelCurrentRequest = useCallback(() => {
    if (provider.currentRequest && typeof provider.currentRequest.cancel === 'function') {
      provider.currentRequest.cancel();
    }
    provider.isProcessingRequest = false;
  }, []);

  // Helper function to transform Algolia results to Shopify products
  const transformProducts = useCallback(async (algoliaResults) => {
    const productIdsList = [...new Set(algoliaResults.hits.map(product => product.id))];
    const transformedProducts = await shopify.getShopifyProductList(productIdsList);
    
    const output = {
      products: transformedProducts,
      page: algoliaResults.page,
      filters: null,
      total_results: null,
      pages: null,
    };

    if (algoliaResults.facets) {
      output.filters = transformFacets(algoliaResults.facets);
    }

    if (algoliaResults.nbHits > -1) {
      output.total_results = algoliaResults.nbHits;
    }

    if (algoliaResults.nbPages > -1) {
      output.pages = algoliaResults.nbPages;
    }

    return output;
  }, []);

  // Helper function to transform facets
  const transformFacets = useCallback((facets) => {
    const sortedFacets = provider.usedFacets;
    
    return Object.entries(facets).reduce((accumulator, [facetKey, facetValues]) => {
      let facetLabel = facetKey.split('.').pop();
      
      if (facetLabel === 'inventory_available') {
        facetLabel = 'availability';
      } else if (facetLabel === 'price_range') {
        facetLabel = 'price';
      }

      const values = Object.entries(facetValues).reduce((acc, [valueKey, valueCount]) => {
        let valueLabel = valueKey;
        
        if (valueLabel === 'true') {
          valueLabel = 'available';
        } else if (valueLabel === 'false') {
          valueLabel = 'unavailable';
        }
        
        acc.push({ key: valueKey, label: valueLabel, count: valueCount });
        return acc;
      }, []).sort((a, b) => {
        if (facetLabel === 'price') {
          return parseFloat(a.label.split(':')[0]) - parseFloat(b.label.split(':')[0]);
        }
        return a.label.localeCompare(b.label);
      });

      accumulator.push({ label: facetLabel, key: facetKey, values });
      return accumulator;
    }, []).sort((a, b) => sortedFacets.indexOf(a.key) - sortedFacets.indexOf(b.key));
  }, []);

  // Perform Algolia product search
  const algoliaProductSearch = useCallback(async (searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) => {
    const index = provider.client.initIndex(provider.config.index[indexKey]);
    const searchParams = {
      ...provider.defaultSearchParams,
      facets: provider.usedFacets,
      ...customSearchParams,
    };

    try {
      const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);
      const results = await transformProducts(algoliaResults);

      window.dispatchEvent(new CustomEvent('searchrequest-products-complete', {
        detail: {
          query: searchQuery,
          products: results.products,
          totalResults: results.total_results,
          filters: results.filters,
          type: provider.requestType,
          page: results.page,
          isFiltering: provider.isFiltering
        },
      }));
      
      provider.isProcessingRequest = false;
      return results;
    } catch (err) {
      console.error('ERROR with Algolia product search:', err);
      provider.isProcessingRequest = false;
      return null;
    }
  }, [transformProducts]);

  // Perform Algolia article search
  const algoliaArticleSearch = useCallback(async (searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) => {
    const index = provider.client.initIndex(provider.config.index[indexKey]);
    const searchParams = {
      ...provider.defaultSearchParams,
      facets: provider.usedFacets,
      ...customSearchParams,
    };
    
    try {
      const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);

      window.dispatchEvent(new CustomEvent('searchrequest-articles-complete', {
        detail: {
          articles: algoliaResults.hits,
          totalResults: algoliaResults.nbHits,
          type: provider.requestType,
        },
      }));
      
      provider.isProcessingRequest = false;
      return algoliaResults;
    } catch (err) {
      console.error('ERROR with Algolia article search:', err);
      provider.isProcessingRequest = false;
      return null;
    }
  }, []);

  // Perform Algolia suggestions search
  const algoliaSuggestionsSearch = useCallback(async (queries) => {
    try {
      const eventDetails = {
        type: provider.requestType,
      };
      
      const algoliaResults = await provider.client.multipleQueries(queries);
      
      Object.keys(provider.config.index).forEach((key) => {
        const indexName = provider.config.index[key];
        const matchingResults = algoliaResults.results.find(
          (result) => result.index.includes(indexName)
        )?.hits || [];
        
        if (matchingResults.length > 0) {
          eventDetails[key] = matchingResults;
        }
      });
      
      window.dispatchEvent(new CustomEvent('searchrequest-suggestions-complete', {
        detail: { ...eventDetails },
      }));
      
      provider.isProcessingRequest = false;
      return algoliaResults;
    } catch (err) {
      console.error('ERROR with Algolia suggestions search:', err);
      provider.isProcessingRequest = false;
      return null;
    }
  }, []);

  // Create the provider object that our base SearchProvider will use
  const algoliaProvider = {
    initialize,
    handleSearchRequest,
    handleSuggestionSearchRequest,
    handleFilterRequest,
    handlePaginationRequest,
  };

  return (
    <SearchProvider provider={algoliaProvider} settings={settings} searchType={searchType}>
      {children}
    </SearchProvider>
  );
}

export default AlgoliaSearchProvider;